import { create } from 'zustand';

// Scanner states enum
export const ScannerState = {
  INITIALIZING: 'INITIALIZING',
  READY: 'READY',
  SCANNING: 'SCANNING',
  PROCESSING: 'PROCESSING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  COOLDOWN: 'COOLDOWN',
};

// Scanner state machine - defines valid state transitions
const scannerStateMachine = {
  [ScannerState.INITIALIZING]: {
    onCameraReady: ScannerState.READY,
  },
  [ScannerState.READY]: {
    onQRDetected: ScannerState.SCANNING,
  },
  [ScannerState.SCANNING]: {
    onValidQR: ScannerState.PROCESSING,
    onInvalidQR: ScannerState.ERROR,
  },
  [ScannerState.PROCESSING]: {
    onAPISuccess: ScannerState.SUCCESS,
    onAPIError: ScannerState.ERROR,
  },
  [ScannerState.SUCCESS]: {
    afterCooldown: ScannerState.READY,
  },
  [ScannerState.ERROR]: {
    afterCooldown: ScannerState.READY,
  },
  [ScannerState.COOLDOWN]: {
    afterCooldown: ScannerState.READY,
  },
};

// Create the scanner store
export const useScannerStore = create((set, get) => ({
  // State
  state: ScannerState.INITIALIZING,
  isScanning: false,
  scanCount: 0,
  lastScanTime: null,
  lastScanData: null,
  errorMessage: null,
  mode: null, // 'checkin' | 'join' | 'adduser' | 'checkout'

  // Performance metrics
  metrics: {
    lastScanDuration: null,
    lastApiDuration: null,
    totalScans: 0,
    successfulScans: 0,
    failedScans: 0,
  },

  // Actions
  initializeScanner: (mode) => {
    set({
      mode,
      state: ScannerState.INITIALIZING,
      isScanning: false,
      errorMessage: null,
      lastScanData: null, // Clear previous scan data
    });
  },

  // State transitions with validation
  transitionTo: (newState, payload = {}) => {
    const currentState = get().state;
    const validTransitions = scannerStateMachine[currentState];

    // Check if transition is valid
    if (
      validTransitions &&
      Object.values(validTransitions).includes(newState)
    ) {
      set({
        state: newState,
        ...payload,
      });

      // Update scanning flag based on state
      if (newState === ScannerState.READY) {
        set({ isScanning: true, errorMessage: null });
      } else if (
        [
          ScannerState.PROCESSING,
          ScannerState.SUCCESS,
          ScannerState.ERROR,
        ].includes(newState)
      ) {
        set({ isScanning: false });
      }

      return true;
    }

    return false;
  },

  // Scanner control actions
  pauseScanning: () => {
    set({ isScanning: false });
  },

  resumeScanning: () => {
    const currentState = get().state;
    if (currentState === ScannerState.READY) {
      set({ isScanning: true });
    }
  },

  // Set camera ready state - useful for INITIALIZING state
  setCameraReady: () => {
    const currentState = get().state;
    if (currentState === ScannerState.INITIALIZING) {
      set({ isScanning: true });
    }
  },

  // Scan result actions
  recordScan: (scanData) => {
    set((state) => ({
      lastScanData: scanData,
      lastScanTime: Date.now(),
      scanCount: state.scanCount + 1,
      metrics: {
        ...state.metrics,
        totalScans: state.metrics.totalScans + 1,
      },
    }));
  },

  recordSuccess: (duration) => {
    set((state) => ({
      metrics: {
        ...state.metrics,
        successfulScans: state.metrics.successfulScans + 1,
        lastScanDuration: duration,
      },
    }));
  },

  recordError: (error) => {
    set((state) => ({
      errorMessage: error.message || 'Unknown error',
      metrics: {
        ...state.metrics,
        failedScans: state.metrics.failedScans + 1,
      },
    }));
  },

  recordApiDuration: (duration) => {
    set((state) => ({
      metrics: {
        ...state.metrics,
        lastApiDuration: duration,
      },
    }));
  },

  // Reset actions
  resetScanner: () => {
    set({
      state: ScannerState.READY,
      isScanning: true,
      errorMessage: null,
      lastScanData: null,
    });
  },

  resetMetrics: () => {
    set({
      metrics: {
        lastScanDuration: null,
        lastApiDuration: null,
        totalScans: 0,
        successfulScans: 0,
        failedScans: 0,
      },
    });
  },

  // Utility getters
  getSuccessRate: () => {
    const { successfulScans, totalScans } = get().metrics;
    return totalScans > 0 ? (successfulScans / totalScans) * 100 : 0;
  },

  getAverageScanTime: () => {
    const { lastScanDuration, successfulScans } = get().metrics;
    // This is simplified - in production you'd track all scan times
    return lastScanDuration || 0;
  },
}));

// Export convenience hooks
export const useScannerState = () => useScannerStore((state) => state.state);
export const useScannerMode = () => useScannerStore((state) => state.mode);
export const useScannerMetrics = () =>
  useScannerStore((state) => state.metrics);
export const useIsScanning = () => useScannerStore((state) => state.isScanning);
